import {
  Dropdown,
  FormItem,
  Modal,
  TextField,
} from '@getsynapse/design-system';
import { ModalProps } from '@getsynapse/design-system/dist/Organisms/Modal/Modal';
import { get } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import intl from 'react-intl-universal';
import { useDispatch, useSelector } from 'react-redux';
import {
  createNewBudget,
  selectBudgetSettings,
} from '../../../../../../state/BudgetList/budgetListSlice';
import { LONG_INPUTS_LENGTH, MONTHS, PATHS } from 'utils/constants';
import {
  BudgetModel,
  CreateBudgetRequest,
  Option,
} from '../../../../../../utils/customTypes';
import { getInitialValueForDropDown } from 'utils/functions';
export type CreateBudgetModalProps = ModalProps & {
  onCreateBudget: (data: BudgetModel) => void;
  onCancel: () => void;
};

export const CreateBudgetModal = ({
  onCreateBudget,
  onCancel,
  ...props
}: CreateBudgetModalProps) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const budgetSettings = useSelector(selectBudgetSettings);

  useEffect(() => {
    setBudgetData({} as CreateBudgetRequest);
  }, [props.isOpen]);

  const [budgetData, setBudgetData] = useState<CreateBudgetRequest>(
    {} as CreateBudgetRequest
  );

  const toSentenceCase = (str: string) => {
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  const fiscalYearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    if (!budgetSettings) {
      return [];
    }

    const thisFiscalYearOptions = [];

    const fiscalMonthStart: number = budgetSettings?.fiscalYearStartMonth ?? 0;
    const monthName = toSentenceCase(MONTHS[fiscalMonthStart]);
    const monthValue = (fiscalMonthStart + 1).toString().padStart(2, '0');

    for (let i = -1; i <= 1; i++) {
      const year = currentYear + i;

      thisFiscalYearOptions.push({
        label: `${monthName} ${year}`,
        value: `${year}-${monthValue}-01`,
      });
    }

    return thisFiscalYearOptions;
  }, [budgetSettings]);

  async function createBudget() {
    const data = await dispatch(createNewBudget({ ...budgetData }));

    const createdBudget = get(data, 'payload.data', null);
    if (createdBudget) {
      onCreateBudget(createdBudget);
      const budgetId = get(createdBudget, 'id', null);
      history.push(`${PATHS.BUDGETS}/${budgetId}`);
      setBudgetData({} as CreateBudgetRequest);
    }
  }

  const onFieldChange = (prop: string, value: string | string[]) => {
    setBudgetData((prevState) => ({
      ...prevState,
      [prop]: value,
    }));
  };

  const handlePickYearStart = (year: Option) => {
    onFieldChange('fiscalPeriodStart', year.value);
  };

  return (
    <Modal
      {...props}
      data-cy='create-budget-modal'
      aria-label={intl.get('BUDGET_PAGE.CREATE_BUDGET_MODAL.TITLE')}
      titleIcon={{
        name: 'add-circle-outline',
      }}
      title={intl.get('BUDGET_PAGE.CREATE_BUDGET_MODAL.TITLE')}
      size='small'
      childrenClassName='pr-0'
      actionButtons={[
        {
          children: intl.get('BUDGET_PAGE.CREATE_BUDGET_MODAL.TITLE'),
          onClick: createBudget,
          disabled: !budgetData.title || !budgetData.fiscalPeriodStart,
          variant: 'primary',
          'data-cy': 'create-budget_button',
        },
        {
          children: intl.get('CANCEL'),
          variant: 'tertiary',
          onClick: onCancel,
          'data-cy': 'cancel-button',
        },
      ]}
    >
      <div className='flex flex-col gap-y-4'>
        <FormItem>
          <TextField
            labelProps={{ required: true }}
            label={intl.get('BUDGET_PAGE.CREATE_BUDGET_MODAL.FORM.NAME')}
            displayCharactersCount
            maxLength={LONG_INPUTS_LENGTH}
            defaultValue={budgetData.title}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              onFieldChange('title', event.target.value)
            }
            placeholder={intl.get(
              'BUDGET_PAGE.CREATE_BUDGET_MODAL.PLACEHOLDERS.NAME'
            )}
            className='w-full'
            data-cy='budget_title-name'
          />
        </FormItem>

        <FormItem
          label={intl.get('BUDGET_PAGE.CREATE_BUDGET_MODAL.FORM.START')}
        >
          <Dropdown
            onChange={handlePickYearStart}
            options={fiscalYearOptions}
            values={getInitialValueForDropDown(
              fiscalYearOptions,
              budgetData.fiscalPeriodStart
            )}
            triggerProps={{
              'data-cy': 'budget_start-date',
              placeholder: intl.get(
                'BUDGET_PAGE.CREATE_BUDGET_MODAL.PLACEHOLDERS.START'
              ),
            }}
          />
        </FormItem>
      </div>
    </Modal>
  );
};
